/**
 * Question Editor
 * Full-featured form for creating and editing questions
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { ConditionalLogicBuilder } from './ConditionalLogicBuilder';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { 
  Save, 
  X, 
  AlertCircle, 
  Eye,
  FileText,
  ListChecks,
  CheckSquare,
  Hash,
  Calendar,
  AlignLeft,
  ToggleLeft,
  ListPlus
} from 'lucide-react';
import { OptionsEditor } from './OptionsEditor';
import { ValidationRulesEditor } from './ValidationRulesEditor';
import { formatCategoryLabel } from '../../utils/normalizeAcronyms';
import type { Question } from '../../utils/api/questionBankApi';
import { cn } from '../../lib/utils';
import { SplitButton } from '../ui/split-button';
import { Copy } from 'lucide-react';

interface QuestionEditorProps {
  question?: Question;
  firmId: string;
  onSave: (question: Partial<Question>) => void;
  onSaveAs?: (question: Partial<Question>) => void;
  onCancel: () => void;
}

// Default new question
const createDefaultQuestion = (): Partial<Question> => ({
  text: '',
  type: 'text',
  category: 'general',
  required: false,
  critical: false,
  helpText: '',
  placeholder: '',
  description: '',
  grouping: {
    isPartOfWizard: false,
    gridBehavior: 'both',
  },
  requiresContext: false,
  tags: [],
  phase: 'discovery',
  source: 'consultant_custom',
  examples: [],
});

// Question types with icons
const questionTypes = [
  { value: 'text', label: 'Text', icon: FileText, description: 'Single line text input' },
  { value: 'textarea', label: 'Textarea', icon: AlignLeft, description: 'Multi-line text input' },
  { value: 'select', label: 'Select', icon: ListChecks, description: 'Dropdown with single choice' },
  { value: 'multiselect', label: 'Multi-select', icon: CheckSquare, description: 'Multiple choice options' },
  { value: 'boolean', label: 'Yes/No', icon: ToggleLeft, description: 'True/false toggle' },
  { value: 'number', label: 'Number', icon: Hash, description: 'Numeric input' },
  { value: 'date', label: 'Date', icon: Calendar, description: 'Date picker' },
  { value: 'list_builder', label: 'List Builder', icon: ListPlus, description: 'Build a list with "Add another" button' },
];

// Categories
const categories = [
  'general',
  'service_requests',
  'incidents',
  'problems',
  'changes',
  'workflows',
  'queues',
  'slas',
  'automations',
  'reporting',
  'integrations',
  'permissions',
  'custom_fields',
  'notifications',
];

export function QuestionEditor({
  question,
  firmId,
  onSave,
  onSaveAs,
  onCancel,
}: QuestionEditorProps) {
  const [formData, setFormData] = useState<Partial<Question>>(
    question || createDefaultQuestion()
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPreview, setShowPreview] = useState(false);

  // Update form when question prop changes
  useEffect(() => {
    if (question) {
      setFormData(question);
    }
  }, [question]);

  // Update a field
  const updateField = <K extends keyof Question>(field: K, value: Question[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field as string]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as string];
        return newErrors;
      });
    }
  };

  // Update grouping metadata
  const updateGrouping = <K extends keyof Question['grouping']>(
    field: K,
    value: Question['grouping'][K]
  ) => {
    setFormData(prev => ({
      ...prev,
      grouping: {
        ...prev.grouping!,
        [field]: value,
      },
    }));
  };

  // Add a tag
  const addTag = (tag: string) => {
    if (tag && !formData.tags?.includes(tag)) {
      updateField('tags', [...(formData.tags || []), tag]);
    }
  };

  // Remove a tag
  const removeTag = (tag: string) => {
    updateField('tags', formData.tags?.filter(t => t !== tag) || []);
  };

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.text?.trim()) {
      newErrors.text = 'Question text is required';
    }

    if (!formData.id && question?.id) {
      // Editing mode - ID should exist
    } else if (!formData.id) {
      // Creating mode - generate ID from text
      const generatedId = formData.text
        ?.toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 50);
      if (!generatedId) {
        newErrors.id = 'Cannot generate ID from question text';
      } else {
        formData.id = generatedId;
      }
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    // Validate options for select/multiselect
    if (
      (formData.type === 'select' || formData.type === 'multiselect') &&
      (!formData.options || formData.options.length === 0)
    ) {
      newErrors.options = 'Options are required for select/multiselect questions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = () => {
    if (validate()) {
      // Set metadata
      const now = new Date().toISOString();
      const questionToSave: Partial<Question> = {
        ...formData,
        lastModified: now,
      };

      if (!question) {
        // New question
        questionToSave.createdAt = now;
      }

      onSave(questionToSave);
    }
  };

  const handleSaveAs = () => {
    if (validate()) {
      // Set metadata
      const now = new Date().toISOString();
      const questionToSave: Partial<Question> = {
        ...formData,
        lastModified: now,
      };

      if (!question) {
        // New question
        questionToSave.createdAt = now;
      }

      onSaveAs?.(questionToSave);
    }
  };

  const isEditMode = !!question;
  const selectedType = questionTypes.find(t => t.value === formData.type);

  return (
    <div className="flex flex-col h-full overflow-visible">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b overflow-visible">
        <div>
          <h2 className="text-2xl font-bold">
            {isEditMode ? 'Edit Question' : 'Create Question'}
          </h2>
          {formData.id && (
            <code className="text-sm text-muted-foreground">{formData.id}</code>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
          >
            <Eye className="w-4 h-4 mr-2" />
            {showPreview ? 'Hide' : 'Show'} Preview
          </Button>
          <Button variant="outline" onClick={onCancel}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleSaveAs}
          >
            <Copy className="w-4 h-4 mr-2" />
            Clone Question
          </Button>
          <Button
            onClick={handleSave}
          >
            <Save className="w-4 h-4 mr-2" />
            Save
          </Button>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto">
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="options">Options & Validation</TabsTrigger>
              <TabsTrigger value="grouping">Grouping & Wizards</TabsTrigger>
              <TabsTrigger value="advanced">Advanced</TabsTrigger>
            </TabsList>

            {/* BASIC INFO TAB */}
            <TabsContent value="basic" className="space-y-6 mt-6">
              {/* Question Text */}
              <div className="space-y-2">
                <Label htmlFor="text" className="required">
                  Question Text *
                </Label>
                <Textarea
                  id="text"
                  value={formData.text || ''}
                  onChange={(e) => updateField('text', e.target.value)}
                  placeholder="What is your question?"
                  rows={3}
                  className={errors.text ? 'border-destructive' : ''}
                />
                {errors.text && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.text}
                  </p>
                )}
              </div>

              {/* Question Type */}
              <div className="space-y-2">
                <Label htmlFor="type">Question Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => updateField('type', value as Question['type'])}
                >
                  <SelectTrigger id="type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {questionTypes.map((type) => {
                      const Icon = type.icon;
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            <div>
                              <div className="font-medium">{type.label}</div>
                              <div className="text-xs text-muted-foreground">
                                {type.description}
                              </div>
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Category & Phase */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => updateField('category', value)}
                  >
                    <SelectTrigger id="category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {formatCategoryLabel(cat)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.category && (
                    <p className="text-sm text-destructive">{errors.category}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phase">Phase</Label>
                  <Select
                    value={formData.phase}
                    onValueChange={(value) => updateField('phase', value as Question['phase'])}
                  >
                    <SelectTrigger id="phase">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="discovery">Discovery</SelectItem>
                      <SelectItem value="scoping">Scoping</SelectItem>
                      <SelectItem value="implementation">Implementation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Placeholder & Help Text */}
              <div className="space-y-2">
                <Label htmlFor="placeholder">Placeholder Text</Label>
                <Input
                  id="placeholder"
                  value={formData.placeholder || ''}
                  onChange={(e) => updateField('placeholder', e.target.value)}
                  placeholder="Enter placeholder text..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="helpText">Help Text</Label>
                <Textarea
                  id="helpText"
                  value={formData.helpText || ''}
                  onChange={(e) => updateField('helpText', e.target.value)}
                  placeholder="Provide guidance to help users answer this question..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (Internal)</Label>
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Internal notes about this question..."
                  rows={2}
                />
              </div>

              {/* Flags */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="required"
                    checked={formData.required}
                    onCheckedChange={(checked) => updateField('required', !!checked)}
                  />
                  <Label htmlFor="required" className="cursor-pointer">
                    Required field
                  </Label>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="critical"
                    checked={formData.critical}
                    onCheckedChange={(checked) => updateField('critical', !!checked)}
                  />
                  <Label htmlFor="critical" className="cursor-pointer">
                    Critical question (must be answered for completeness)
                  </Label>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="requiresContext"
                    checked={formData.requiresContext}
                    onCheckedChange={(checked) => updateField('requiresContext', !!checked)}
                  />
                  <Label htmlFor="requiresContext" className="cursor-pointer">
                    Requires AI context (for discovery assistant)
                  </Label>
                </div>
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.tags?.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => removeTag(tag)}
                    >
                      {tag}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a tag..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTag(e.currentTarget.value);
                        e.currentTarget.value = '';
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    onClick={(e) => {
                      const input = e.currentTarget.previousSibling as HTMLInputElement;
                      addTag(input.value);
                      input.value = '';
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* OPTIONS & VALIDATION TAB */}
            <TabsContent value="options" className="space-y-6 mt-6">
              {(formData.type === 'select' || formData.type === 'multiselect') && (
                <OptionsEditor
                  options={formData.options || []}
                  onChange={(options) => updateField('options', options)}
                  error={errors.options}
                />
              )}

              <ValidationRulesEditor
                validation={formData.validation}
                questionType={formData.type!}
                onChange={(validation) => updateField('validation', validation)}
              />

              {/* Examples */}
              <div className="space-y-2">
                <Label>Example Answers</Label>
                <p className="text-sm text-muted-foreground">
                  Provide example answers to guide users
                </p>
                {formData.examples?.map((example, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={example}
                      onChange={(e) => {
                        const newExamples = [...(formData.examples || [])];
                        newExamples[index] = e.target.value;
                        updateField('examples', newExamples);
                      }}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newExamples = formData.examples?.filter((_, i) => i !== index);
                        updateField('examples', newExamples);
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => {
                    updateField('examples', [...(formData.examples || []), '']);
                  }}
                >
                  Add Example
                </Button>
              </div>
            </TabsContent>

            {/* GROUPING & WIZARDS TAB */}
            <TabsContent value="grouping" className="space-y-6 mt-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="isPartOfWizard"
                    checked={formData.grouping?.isPartOfWizard}
                    onCheckedChange={(checked) => updateGrouping('isPartOfWizard', !!checked)}
                  />
                  <Label htmlFor="isPartOfWizard" className="cursor-pointer">
                    Include in wizard
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gridBehavior">Display Behavior</Label>
                  <Select
                    value={formData.grouping?.gridBehavior || 'both'}
                    onValueChange={(value) => 
                      updateGrouping('gridBehavior', value as 'inline' | 'wizard_only' | 'both')
                    }
                  >
                    <SelectTrigger id="gridBehavior">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inline">Inline only (show in config item grid)</SelectItem>
                      <SelectItem value="wizard_only">Wizard only (hide in grid)</SelectItem>
                      <SelectItem value="both">Both (show everywhere)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.grouping?.isPartOfWizard && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="wizardName">Wizard Name</Label>
                      <Input
                        id="wizardName"
                        value={formData.grouping?.wizardName || ''}
                        onChange={(e) => updateGrouping('wizardName', e.target.value)}
                        placeholder="Discovery Wizard"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="pageNumber">Page Number</Label>
                        <Input
                          id="pageNumber"
                          type="number"
                          value={formData.grouping?.pageNumber || 1}
                          onChange={(e) => updateGrouping('pageNumber', parseInt(e.target.value))}
                          min={1}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="questionIndex">Question Order</Label>
                        <Input
                          id="questionIndex"
                          type="number"
                          value={formData.grouping?.questionIndex || 0}
                          onChange={(e) => updateGrouping('questionIndex', parseInt(e.target.value))}
                          min={0}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </TabsContent>

            {/* ADVANCED TAB */}
            <TabsContent value="advanced" className="space-y-6 mt-6">
              <ConditionalLogicBuilder
                conditionalOn={formData.conditionalOn}
                onChange={(conditional) => updateField('conditionalOn', conditional)}
                availableQuestions={[]} // TODO: Pass actual questions
              />

              <div className="space-y-2">
                <Label htmlFor="relatedQuestions">Related Questions</Label>
                <p className="text-sm text-muted-foreground">
                  Questions that are related to this one (comma-separated IDs)
                </p>
                <Input
                  id="relatedQuestions"
                  value={formData.relatedQuestionIds?.join(', ') || ''}
                  onChange={(e) => {
                    const ids = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                    updateField('relatedQuestionIds', ids);
                  }}
                  placeholder="sr_types, wf_statuses"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="section">Section</Label>
                <Input
                  id="section"
                  value={formData.section || ''}
                  onChange={(e) => updateField('section', e.target.value)}
                  placeholder="General Information"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="order">Display Order</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order || 0}
                  onChange={(e) => updateField('order', parseInt(e.target.value))}
                  min={0}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Preview Panel */}
      {showPreview && (
        <div className="border-t p-6 bg-muted/30">
          <h3 className="font-medium mb-4">Preview</h3>
          <div className="max-w-2xl">
            <Label>{formData.text || 'Question text will appear here'}</Label>
            {formData.helpText && (
              <p className="text-sm text-muted-foreground mt-1">{formData.helpText}</p>
            )}
            <div className="mt-2">
              {formData.type === 'text' && (
                <Input placeholder={formData.placeholder} disabled />
              )}
              {formData.type === 'textarea' && (
                <Textarea placeholder={formData.placeholder} disabled rows={3} />
              )}
              {formData.type === 'select' && (
                <Select disabled>
                  <SelectTrigger>
                    <SelectValue placeholder={formData.placeholder || 'Select an option'} />
                  </SelectTrigger>
                </Select>
              )}
              {formData.type === 'number' && (
                <Input type="number" placeholder={formData.placeholder} disabled />
              )}
              {formData.type === 'date' && (
                <Input type="date" disabled />
              )}
              {formData.type === 'boolean' && (
                <div className="flex items-center gap-2">
                  <Checkbox disabled />
                  <span className="text-sm">Yes / No</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}